import type { PageBlock } from './types';

const IMAGE = /^!\[([^\]]*)\]\(media:([A-Za-z0-9_-]+)(?: "([^"]*)")?\)$/;

function blockToText(block: PageBlock): string {
  switch (block.type) {
    case 'heading': return `${block.level === 3 ? '###' : '##'} ${block.text}`;
    case 'paragraph': return block.text;
    case 'list': return block.items.map((item, index) => (block.ordered ? `${index + 1}. ${item}` : `- ${item}`)).join('\n');
    case 'quote': return block.attribution ? `> ${block.text}\n— ${block.attribution}` : `> ${block.text}`;
    case 'image': return `![${block.altText}](media:${block.mediaId}${block.caption ? ` "${block.caption}"` : ''})`;
  }
}

export function blocksToText(blocks: PageBlock[]): string {
  return blocks.map(blockToText).join('\n\n');
}

function chunkToBlock(chunk: string, id: string): PageBlock {
  const lines = chunk.split('\n').map((line) => line.trim()).filter(Boolean);
  const first = lines[0];
  if (first.startsWith('### ')) return { id, type: 'heading', level: 3, text: first.slice(4).trim() };
  if (first.startsWith('## ')) return { id, type: 'heading', level: 2, text: first.slice(3).trim() };
  const image = IMAGE.exec(first);
  if (image && lines.length === 1) return { id, type: 'image', altText: image[1], mediaId: image[2], caption: image[3] ?? '' };
  if (first.startsWith('> ')) {
    const attributionLine = lines.find((line) => line.startsWith('— '));
    const text = lines.filter((line) => line !== attributionLine).map((line) => line.replace(/^>\s?/, '')).join(' ');
    return { id, type: 'quote', text, attribution: attributionLine ? attributionLine.slice(2).trim() : '' };
  }
  if (lines.every((line) => /^\d+\.\s/.test(line))) return { id, type: 'list', ordered: true, items: lines.map((line) => line.replace(/^\d+\.\s+/, '')) };
  if (lines.every((line) => line.startsWith('- '))) return { id, type: 'list', ordered: false, items: lines.map((line) => line.slice(2).trim()) };
  return { id, type: 'paragraph', text: lines.join(' ') };
}

export function textToBlocks(text: string): PageBlock[] {
  return text.split(/\n\s*\n/).map((chunk) => chunk.trim()).filter(Boolean).map((chunk, index) => chunkToBlock(chunk, `b${index + 1}`));
}
